/**
 * The emoji a team member is marked with on the console's calendar — with NO
 * runtime dependencies.
 *
 * Ported from tperent (`backend/src/lib/calendar-marks.ts` and the client's
 * `StaffMark.tsx`), where the same rule had to be written twice because the
 * two packages had nothing in common. Here it is one module, reachable as
 * `@fitting-room/contracts/calendar-marks`, for the reason `./intake.ts` is
 * its own: the console needs the SAME answer the API enforces — so a bad paste
 * is answered in the picker rather than by a 400 — and pulling it through the
 * barrel would drag every zod schema into the client bundle.
 */

/**
 * The picker's shortcut, NOT the set of legal values: any single emoji may be
 * pasted (see `isCalendarMark`).
 *
 * ORDER IS PART OF THE DESIGN. A small shop only ever reaches the first few,
 * so the list front-loads contrast — pink bloom, blue butterfly, gold crown,
 * green clover, cyan gem — and lets similar marks fall late, where only a shop
 * large enough to also need a legend reaches them. White-bodied emoji are left
 * out: they wash out on the console's paper ground.
 */
export const STAFF_MARKS = [
  '🌸', '🦋', '👑', '🍀', '💎',
  '🌻', '🎀', '🍾', '💍', '🌹',
  '🕯️', '📷', '🌷', '🔔', '🧵',
  '🥂', '🎂', '✂️', '💐', '🌙',
] as const

/**
 * A mark renders into a calendar cell a few pixels tall with nowhere to wrap,
 * so the one thing this must guarantee is a single glyph. Text and two-emoji
 * strings are both out.
 *
 * `Emoji_Component` covers ZWJ, VS16, skin tones and regional indicators, but
 * also bare digits — hence the separate pictograph requirement, with flags and
 * keycaps (which contain no pictograph) handled on their own.
 */
const HAS_PICTOGRAPH = /\p{Extended_Pictographic}/u
const SEQUENCE_CHARS = /^[\p{Extended_Pictographic}\p{Emoji_Component}]+$/u
const FLAG = /^\p{Regional_Indicator}{2}$/u
const KEYCAP = /^[0-9#*]️?⃣$/u
/** The widest real emoji is ten code points; anything past this is someone probing. */
const MAX_CODE_POINTS = 12

function graphemeCount(value: string): number {
  let n = 0
  for (const _ of new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(value)) n++
  return n
}

/** True for exactly one emoji: a plain one, a ZWJ or skin-tone sequence, a flag or a keycap. */
export function isCalendarMark(value: string): boolean {
  if (value === '' || [...value].length > MAX_CODE_POINTS) return false
  if (graphemeCount(value) !== 1) return false
  if (FLAG.test(value) || KEYCAP.test(value)) return true
  return HAS_PICTOGRAPH.test(value) && SEQUENCE_CHARS.test(value)
}

/**
 * Pasted emoji arrive with whitespace often enough (copied out of a message)
 * that trimming beats refusing. NFC so the same emoji typed on two platforms
 * compares equal under the per-tenant uniqueness constraint.
 */
export function normalizeMark(value: string): string {
  return value.trim().normalize('NFC')
}
